import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateProject } from '../../../api';
import { AnalysisMode, GenerationTemplate, LLMRoleConfigMap, Project } from '../../../types';
import { DEFAULT_CHAR_STATE_PROMPT_PREFIX, DEFAULT_FIXED_IMAGE_PROMPT, parseRefImages } from '../utils/editDefaults';
import { buildProjectUpdatePayload, validateProjectSave } from '../projectSavePayload';
import { useProjectBootstrapOptions } from './useProjectBootstrapOptions';
import { usePublishedLanguageSkills } from './usePublishedLanguageSkills';

type SaveSource = 'project' | 'style';

interface UseProjectConfigStateOptions {
  /** 保存成功后的刷新回调（等价于原 ProjectDetail 中保存后的 loadProject(id)）。*/
  onSaved?: () => void;
}

/**
 * 把原本散落在 ProjectDetail 里、由「项目设置」(EditProjectModal) 和「风格设置」
 * (StyleConfigModal) 共用的一整块配置编辑态 / seeding / 保存 / 应用模板逻辑，
 * 原样搬进这个自包含 hook（按 project 工作），供两个弹窗复用。
 *
 * 行为与迁移前完全一致：
 * - seeding：project 变化时整体从 project 回填（等价于 loadProject + resetEditStateFromProject）。
 * - save：照搬 handleSaveProject —— 全量 base + 全部风格字段一起 PUT（§3-④），
 *   仅把保存成功后的 loadProject(id) 换成 onSaved()。
 * - applyPendingTemplate：照搬原 ProjectConfirmDialogs 里「应用模板确认」的批量回填。
 */
export function useProjectConfigState(project: Project | null, options: UseProjectConfigStateOptions = {}) {
  const { t } = useTranslation('projectDetail');
  const bootstrap = useProjectBootstrapOptions();
  const { scriptSkills } = bootstrap;
  const { langSkills, langSkillsLoading } = usePublishedLanguageSkills();

  // ── 项目基础（EditProjectModal） ──
  const [editName, setEditName] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [editRatio, setEditRatio] = useState('16:9');
  const [editSkillId, setEditSkillId] = useState('');
  const [editSkillVariableValues, setEditSkillVariableValues] = useState<Record<string, string>>({});
  const [editScriptSkillId, setEditScriptSkillId] = useState('');
  const [editScriptSkillValues, setEditScriptSkillValues] = useState<Record<string, string>>({});
  const [editOutputLanguage, setEditOutputLanguage] = useState('');
  const [editLanguageSkillId, setEditLanguageSkillId] = useState('');
  const [editLanguageSkillValues, setEditLanguageSkillValues] = useState<Record<string, string>>({});
  const [editEnableScriptNormalization, setEditEnableScriptNormalization] = useState(false);
  const [editAutoGenerateShots, setEditAutoGenerateShots] = useState(true);
  const [editAnalysisMode, setEditAnalysisMode] = useState<AnalysisMode>('basic');
  const [editAutoStartAnalysis, setEditAutoStartAnalysis] = useState(false);
  const [editEnableVoiceInsertion, setEditEnableVoiceInsertion] = useState(false);
  const [editVoicePrompt, setEditVoicePrompt] = useState('');
  const [editVoicePresetId, setEditVoicePresetId] = useState('');
  const [editDisableBGM, setEditDisableBGM] = useState(false);

  // ── 风格设置（StyleConfigModal） ──
  const [editJimengModel, setEditJimengModel] = useState('jimeng-5.0');
  const [editImagePlatform, setEditImagePlatform] = useState('moyu-jimeng');
  const [editImageModel, setEditImageModel] = useState('jimeng-5.0');
  const [editBaseImagePlatform, setEditBaseImagePlatform] = useState('');
  const [editBaseImageModel, setEditBaseImageModel] = useState('');
  const [editVideoPlatform, setEditVideoPlatform] = useState('moyu-jimeng');
  const [editVideoModel, setEditVideoModel] = useState('seedance-2.0-pro');
  const [editLLMRoleOverrides, setEditLLMRoleOverrides] = useState<Partial<LLMRoleConfigMap>>({});
  const [editAutoChar, setEditAutoChar] = useState(false);
  const [editAutoScene, setEditAutoScene] = useState(false);
  const [editAutoProp, setEditAutoProp] = useState(false);
  const [editCharFixedPrompt, setEditCharFixedPrompt] = useState(DEFAULT_FIXED_IMAGE_PROMPT);
  const [editCharProjectRefImages, setEditCharProjectRefImages] = useState<string[]>([]);
  const [editCharStatePromptPrefix, setEditCharStatePromptPrefix] = useState(DEFAULT_CHAR_STATE_PROMPT_PREFIX);
  const [editSceneStatePromptPrefix, setEditSceneStatePromptPrefix] = useState('');
  const [editPropStatePromptPrefix, setEditPropStatePromptPrefix] = useState('');
  const [editCharStateImageRatio, setEditCharStateImageRatio] = useState('16:9');
  const [editSceneStateImageRatio, setEditSceneStateImageRatio] = useState('16:9');
  const [editPropStateImageRatio, setEditPropStateImageRatio] = useState('16:9');
  const [editBaseConfigCompleted, setEditBaseConfigCompleted] = useState(false);
  const [editVideoPrefixPrompt, setEditVideoPrefixPrompt] = useState('');
  const [editVideoExtraPrompt, setEditVideoExtraPrompt] = useState('');
  const [editSceneFixedPrompt, setEditSceneFixedPrompt] = useState(DEFAULT_FIXED_IMAGE_PROMPT);
  const [editSceneProjectRefImages, setEditSceneProjectRefImages] = useState<string[]>([]);
  const [editPropFixedPrompt, setEditPropFixedPrompt] = useState(DEFAULT_FIXED_IMAGE_PROMPT);
  const [editPropProjectRefImages, setEditPropProjectRefImages] = useState<string[]>([]);
  const [pendingTemplate, setPendingTemplate] = useState<GenerationTemplate | null>(null);
  const [editGenerationTemplateId, setEditGenerationTemplateId] = useState('');

  const [saving, setSaving] = useState(false);

  // ── seeding：从 project 整体回填（等价 loadProject + resetEditStateFromProject） ──
  const resetFromProject = useCallback(() => {
    if (!project) return;
    const p = project;
    setEditName(p.name);
    setEditPrompt(p.projectPrompt || '');
    setEditRatio(p.aspectRatio || '16:9');
    setEditSkillId(p.skillId || '');
    setEditSkillVariableValues(p.skillVariableValues || {});
    setEditScriptSkillId(p.scriptSkillId || '');
    setEditScriptSkillValues(p.scriptSkillValues || {});
    setEditEnableScriptNormalization(p.enableScriptNormalization || false);
    setEditOutputLanguage(p.outputLanguage || '');
    setEditLanguageSkillId(p.languageSkillId || '');
    setEditLanguageSkillValues(p.languageSkillValues || {});
    setEditAutoGenerateShots(p.autoGenerateShots !== false);
    setEditAnalysisMode(p.analysisMode ?? 'basic');
    setEditAutoStartAnalysis(p.autoStartAnalysis || false);
    setEditJimengModel(p.jimengModel || 'jimeng-5.0');
    setEditImagePlatform(p.imagePlatform || 'moyu-jimeng');
    setEditImageModel(p.imageModel || p.jimengModel || 'jimeng-5.0');
    setEditBaseImagePlatform(p.baseImagePlatform || '');
    setEditBaseImageModel(p.baseImageModel || '');
    setEditVideoPlatform(p.videoPlatform || 'moyu-jimeng');
    setEditVideoModel(p.videoModel || 'seedance-2.0-pro');
    setEditLLMRoleOverrides(p.llmRoleOverrides || {});
    setEditAutoChar(p.autoGenerateCharImages || false);
    setEditAutoScene(p.autoGenerateSceneImages || false);
    setEditAutoProp(p.autoGeneratePropImages || false);
    setEditCharFixedPrompt(p.charFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditCharProjectRefImages(parseRefImages(p.charProjectRefImages));
    setEditCharStatePromptPrefix(p.charStatePromptPrefix || DEFAULT_CHAR_STATE_PROMPT_PREFIX);
    setEditSceneStatePromptPrefix(p.sceneStatePromptPrefix || '');
    setEditPropStatePromptPrefix(p.propStatePromptPrefix || '');
    setEditCharStateImageRatio(p.charStateImageRatio || '16:9');
    setEditSceneStateImageRatio(p.sceneStateImageRatio || '16:9');
    setEditPropStateImageRatio(p.propStateImageRatio || '16:9');
    setEditBaseConfigCompleted(p.baseConfigCompleted || false);
    setEditVideoPrefixPrompt(p.videoPrefixPrompt || '');
    setEditVideoExtraPrompt(p.videoExtraPrompt || '');
    setEditEnableVoiceInsertion(p.enableVoiceInsertion || false);
    setEditVoicePrompt(p.voicePrompt || '');
    setEditVoicePresetId(p.voicePresetId || '');
    setEditDisableBGM(p.disableBGM || false);
    setEditGenerationTemplateId(p.generationTemplateId || '');
    setEditSceneFixedPrompt(p.sceneFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditSceneProjectRefImages(parseRefImages(p.sceneProjectRefImages));
    setEditPropFixedPrompt(p.propFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditPropProjectRefImages(parseRefImages(p.propProjectRefImages));
  }, [project]);

  useEffect(() => {
    resetFromProject();
  }, [resetFromProject]);

  // ── 应用模板确认（照搬 ProjectConfirmDialogs 的批量回填） ──
  const applyPendingTemplate = useCallback(() => {
    if (!pendingTemplate) return;
    const template = pendingTemplate;
    setEditJimengModel(template.jimengModel || 'jimeng-5.0');
    if (template.imagePlatform) setEditImagePlatform(template.imagePlatform);
    if (template.imageModel) {
      setEditImageModel(template.imageModel);
      setEditJimengModel(template.imageModel);
    }
    setEditBaseImagePlatform(template.baseImagePlatform || '');
    setEditBaseImageModel(template.baseImageModel || '');
    if (template.videoPlatform) setEditVideoPlatform(template.videoPlatform);
    if (template.videoModel) setEditVideoModel(template.videoModel);
    setEditCharFixedPrompt(template.charFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditCharProjectRefImages(parseRefImages(template.charProjectRefImages));
    setEditSceneFixedPrompt(template.sceneFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditSceneProjectRefImages(parseRefImages(template.sceneProjectRefImages));
    setEditPropFixedPrompt(template.propFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT);
    setEditPropProjectRefImages(parseRefImages(template.propProjectRefImages));
    setEditCharStatePromptPrefix(template.charStatePromptPrefix || DEFAULT_CHAR_STATE_PROMPT_PREFIX);
    setEditSceneStatePromptPrefix(template.sceneStatePromptPrefix || '');
    setEditPropStatePromptPrefix(template.propStatePromptPrefix || '');
    setEditCharStateImageRatio(template.charStateImageRatio || '16:9');
    setEditSceneStateImageRatio(template.sceneStateImageRatio || '16:9');
    setEditPropStateImageRatio(template.propStateImageRatio || '16:9');
    setEditVideoPrefixPrompt(template.videoPrefixPrompt || '');
    setEditVideoExtraPrompt(template.videoExtraPrompt || '');
    setEditDisableBGM(template.disableBGM || false);
    setEditGenerationTemplateId(template.id);
    setPendingTemplate(null);
  }, [pendingTemplate]);

  // ── 保存（照搬 handleSaveProject；loadProject(id) → onSaved()） ──
  const save = useCallback(async (source: SaveSource = 'project', templateOverride?: GenerationTemplate): Promise<boolean> => {
    if (!project) return false;
    const id = project.id;
    const validationError = validateProjectSave({
      source,
      skillId: editSkillId,
      enableScriptNormalization: editEnableScriptNormalization,
      scriptSkillId: editScriptSkillId,
      scriptSkills,
      scriptSkillValues: editScriptSkillValues,
      t,
    });
    if (validationError) {
      alert(validationError);
      return false;
    }
    setSaving(true);
    try {
      const payload = buildProjectUpdatePayload({
        source,
        base: {
          name: editName,
          projectPrompt: editPrompt,
          aspectRatio: editRatio,
          skillId: editSkillId || undefined,
          skillVariableValues: editSkillVariableValues,
          // 原始值直传（空串=清除台词语种），转 undefined 会让后端部分更新保留旧值
          outputLanguage: editOutputLanguage,
          languageSkillId: editLanguageSkillId,
          languageSkillValues: editLanguageSkillValues,
          autoGenerateShots: editAutoGenerateShots,
          analysisMode: editAnalysisMode,
          autoStartAnalysis: editAutoStartAnalysis,
        },
        script: {
          scriptSkillId: editScriptSkillId || undefined,
          scriptSkillValues: editScriptSkillValues,
          enableScriptNormalization: editEnableScriptNormalization,
        },
      });
      const tpl = templateOverride;
      await updateProject(id, {
        ...payload,
        jimengModel: tpl?.imageModel || editJimengModel,
        imagePlatform: tpl?.imagePlatform || editImagePlatform,
        imageModel: tpl?.imageModel || editImageModel,
        baseImagePlatform: tpl ? (tpl.baseImagePlatform || '') : editBaseImagePlatform,
        baseImageModel: tpl ? (tpl.baseImageModel || '') : editBaseImageModel,
        videoPlatform: tpl?.videoPlatform || editVideoPlatform,
        videoModel: tpl?.videoModel || editVideoModel,
        llmRoleOverrides: editLLMRoleOverrides,
        autoGenerateCharImages: editAutoChar,
        autoGenerateSceneImages: editAutoScene,
        autoGeneratePropImages: editAutoProp,
        charFixedPrompt: tpl ? (tpl.charFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT) : editCharFixedPrompt,
        charStatePromptPrefix: tpl ? (tpl.charStatePromptPrefix || DEFAULT_CHAR_STATE_PROMPT_PREFIX) : editCharStatePromptPrefix,
        sceneStatePromptPrefix: tpl ? (tpl.sceneStatePromptPrefix || '') : editSceneStatePromptPrefix,
        propStatePromptPrefix: tpl ? (tpl.propStatePromptPrefix || '') : editPropStatePromptPrefix,
        charStateImageRatio: tpl ? (tpl.charStateImageRatio || '16:9') : editCharStateImageRatio,
        sceneStateImageRatio: tpl ? (tpl.sceneStateImageRatio || '16:9') : editSceneStateImageRatio,
        propStateImageRatio: tpl ? (tpl.propStateImageRatio || '16:9') : editPropStateImageRatio,
        baseConfigCompleted: tpl ? true : editBaseConfigCompleted,
        videoPrefixPrompt: (tpl ? (tpl.videoPrefixPrompt || '') : editVideoPrefixPrompt) || undefined,
        videoExtraPrompt: (tpl ? (tpl.videoExtraPrompt || '') : editVideoExtraPrompt) || undefined,
        sceneFixedPrompt: tpl ? (tpl.sceneFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT) : editSceneFixedPrompt,
        propFixedPrompt: tpl ? (tpl.propFixedPrompt || DEFAULT_FIXED_IMAGE_PROMPT) : editPropFixedPrompt,
        charProjectRefImages: tpl ? (tpl.charProjectRefImages || '[]') : JSON.stringify(editCharProjectRefImages),
        sceneProjectRefImages: tpl ? (tpl.sceneProjectRefImages || '[]') : JSON.stringify(editSceneProjectRefImages),
        propProjectRefImages: tpl ? (tpl.propProjectRefImages || '[]') : JSON.stringify(editPropProjectRefImages),
        enableVoiceInsertion: source === 'project' ? editEnableVoiceInsertion : undefined,
        voicePrompt: source === 'project' ? (editEnableVoiceInsertion ? editVoicePrompt : '') : undefined,
        voicePresetId: source === 'project' ? (editEnableVoiceInsertion ? editVoicePresetId : '') : undefined,
        disableBGM: source === 'project' ? editDisableBGM : undefined,
        generationTemplateId: tpl?.id || editGenerationTemplateId || undefined,
      });
      options.onSaved?.();
      return true;
    } catch (err) {
      console.error('Failed to update project:', err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    project, t, scriptSkills, options,
    editName, editPrompt, editRatio, editSkillId, editSkillVariableValues, editScriptSkillId, editScriptSkillValues,
    editOutputLanguage, editLanguageSkillId, editLanguageSkillValues, editEnableScriptNormalization,
    editAutoGenerateShots, editAnalysisMode, editAutoStartAnalysis,
    editJimengModel, editImagePlatform, editImageModel, editBaseImagePlatform, editBaseImageModel,
    editVideoPlatform, editVideoModel, editLLMRoleOverrides, editAutoChar, editAutoScene, editAutoProp,
    editCharFixedPrompt, editCharStatePromptPrefix, editSceneStatePromptPrefix,
    editPropStatePromptPrefix, editCharStateImageRatio, editSceneStateImageRatio, editPropStateImageRatio,
    editBaseConfigCompleted, editVideoPrefixPrompt, editVideoExtraPrompt,
    editSceneFixedPrompt, editPropFixedPrompt,
    editCharProjectRefImages, editSceneProjectRefImages, editPropProjectRefImages,
    editEnableVoiceInsertion, editVoicePrompt, editVoicePresetId, editDisableBGM, editGenerationTemplateId,
  ]);

  return {
    bootstrap,
    langSkills,
    langSkillsLoading,
    saving,
    pendingTemplate,
    setPendingTemplate,
    applyPendingTemplate,
    resetFromProject,
    save,
    // 项目基础
    editName, setEditName,
    editPrompt, setEditPrompt,
    editRatio, setEditRatio,
    editSkillId, setEditSkillId,
    editSkillVariableValues, setEditSkillVariableValues,
    editScriptSkillId, setEditScriptSkillId,
    editScriptSkillValues, setEditScriptSkillValues,
    editOutputLanguage, setEditOutputLanguage,
    editLanguageSkillId, setEditLanguageSkillId,
    editLanguageSkillValues, setEditLanguageSkillValues,
    editEnableScriptNormalization, setEditEnableScriptNormalization,
    editAutoGenerateShots, setEditAutoGenerateShots,
    editAnalysisMode, setEditAnalysisMode,
    editAutoStartAnalysis, setEditAutoStartAnalysis,
    editEnableVoiceInsertion, setEditEnableVoiceInsertion,
    editVoicePrompt, setEditVoicePrompt,
    editVoicePresetId, setEditVoicePresetId,
    editDisableBGM, setEditDisableBGM,
    // 风格
    editJimengModel, setEditJimengModel,
    editImagePlatform, setEditImagePlatform,
    editImageModel, setEditImageModel,
    editBaseImagePlatform, setEditBaseImagePlatform,
    editBaseImageModel, setEditBaseImageModel,
    editVideoPlatform, setEditVideoPlatform,
    editVideoModel, setEditVideoModel,
    editLLMRoleOverrides, setEditLLMRoleOverrides,
    editAutoChar, setEditAutoChar,
    editAutoScene, setEditAutoScene,
    editAutoProp, setEditAutoProp,
    editCharFixedPrompt, setEditCharFixedPrompt,
    editCharProjectRefImages, setEditCharProjectRefImages,
    editCharStatePromptPrefix, setEditCharStatePromptPrefix,
    editSceneStatePromptPrefix, setEditSceneStatePromptPrefix,
    editPropStatePromptPrefix, setEditPropStatePromptPrefix,
    editCharStateImageRatio, setEditCharStateImageRatio,
    editSceneStateImageRatio, setEditSceneStateImageRatio,
    editPropStateImageRatio, setEditPropStateImageRatio,
    editBaseConfigCompleted, setEditBaseConfigCompleted,
    editVideoPrefixPrompt, setEditVideoPrefixPrompt,
    editVideoExtraPrompt, setEditVideoExtraPrompt,
    editSceneFixedPrompt, setEditSceneFixedPrompt,
    editSceneProjectRefImages, setEditSceneProjectRefImages,
    editPropFixedPrompt, setEditPropFixedPrompt,
    editPropProjectRefImages, setEditPropProjectRefImages,
    editGenerationTemplateId, setEditGenerationTemplateId,
  };
}
